import { Controller } from "@hotwired/stimulus";
import DataTable, { Config } from "datatables.net-bs5";

type SelectionConfig = {
  checkboxSelector?: string;
  selectAllSelector?: string;
  allowEmptySelection?: boolean;
};

/*
 * Base class for views that use datatable. Sets certain defaults to reduce duplication.
 * To use this controller, add `data-controller='base-datatable` to the div holding the table.
 *
 *
 * To override the config, create a new controller and extend from this class.
 *
 * ============= Example ============

  ```ts
  import { BaseDataTableController } from "./base_datatable_controller";

  export default class extends BaseDataTableController {
    static targets = [];

    connect() {
      super.setupTable({ // Modify the default config to enable ordering and searching, and to center all columns
        ordering: true,
        searching: true,
        columnDefs: [
          {
            targets: ["_all"],
            className: "dt-center",
          },
        ],
      });
      console.debug("Connected to Guest Controller");
    }

    // Controller action to dynamically add data to the table
    // Use jQuery to access the datatable api
    addToRow(data: string[]) {
      $(".datatable").dataTable().api().rows.add([data]);
    }
  }
  ```
 *
 */

export default class extends Controller {
  protected table?: any;
  #selectionConfig: Required<SelectionConfig> = {
    checkboxSelector: 'input[name="selected_items[]"]',
    selectAllSelector: "[data-datatable-select-all]",
    allowEmptySelection: true,
  };

  #defaultConfig: Config = {
    dom: '<"d-flex justify-content-between align-items-center mb-2"<"me-1"f>B>lrtip"',
    buttons: [
      {
        extend: "csv",
        text: '<i class="d-none d-sm-inline ri ri-file-excel-2-fill align-bottom"></i> Export',
        className: "btn btn-sm btn-info",
        exportOptions: {
          columns: ":not(.no-export)", // Exclude columns with no-export class
        },
      },
    ],
    scrollX: true,
    // lengthMenu: [20, 50, 100],
    searching: true,
    lengthChange: false,
    autoWidth: true,
    ordering: false,
    columnDefs: [
      {
        targets: [-1],
        className: "dt-center",
      },
    ],
  };

  connect() {
    console.debug("Connected to Base DataTable");
    this.setupTable();
  }

  setupTable(config?: Config) {
    this.table = new DataTable("#datatable", {
      ...this.#defaultConfig,
      ...(config || {}),
    });

    this.table.draw();
    return this.table;
  }

  setupSelectableTable(config?: Config, selectionConfig?: SelectionConfig) {
    this.#selectionConfig = {
      ...this.#selectionConfig,
      ...(selectionConfig || {}),
    };

    const table = this.setupTable(config);
    table.on("draw", () => this.syncSelectionControls());
    this.syncSelectionControls();

    return table;
  }

  toggleAllSelections(event: Event) {
    const checked = (event.currentTarget as HTMLInputElement).checked;

    this.selectionCheckboxes().forEach((checkbox) => {
      checkbox.checked = checked;
    });

    this.syncSelectionControls();
  }

  syncSelectionControls() {
    const checkboxes = this.selectionCheckboxes();
    const checkedCount = this.selectedSelectionCheckboxes().length;

    this.selectAllCheckboxes().forEach((checkbox) => {
      checkbox.checked =
        checkboxes.length > 0 && checkedCount === checkboxes.length;
      checkbox.indeterminate =
        checkedCount > 0 && checkedCount < checkboxes.length;
    });
  }

  protected selectedSelectionCheckboxes() {
    return this.selectionCheckboxes().filter((checkbox) => checkbox.checked);
  }

  protected selectionAllowed() {
    return (
      this.#selectionConfig.allowEmptySelection ||
      this.selectedSelectionCheckboxes().length > 0
    );
  }

  protected appendSelectedValues(container: HTMLElement, inputName: string) {
    container.replaceChildren();

    this.selectedSelectionCheckboxes().forEach((checkbox) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = inputName;
      input.value = checkbox.value;
      container.appendChild(input);
    });
  }

  protected selectionCheckboxes(): HTMLInputElement[] {
    if (this.table) {
      return this.table
        .rows()
        .nodes()
        .toArray()
        .flatMap((row: HTMLTableRowElement) =>
          Array.from(
            row.querySelectorAll<HTMLInputElement>(
              this.#selectionConfig.checkboxSelector,
            ),
          ),
        );
    }

    return Array.from(
      this.element.querySelectorAll<HTMLInputElement>(
        this.#selectionConfig.checkboxSelector,
      ),
    );
  }

  protected selectAllCheckboxes(): HTMLInputElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLInputElement>(
        this.#selectionConfig.selectAllSelector,
      ),
    );
  }
}
